import React, { useEffect, useState } from 'react';
import { styled, alpha } from '@mui/material/styles';
import {
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  RadioGroup,
  Radio,
  FormControlLabel,
  Button,
  Box,
  Chip,
  Stack,
  Pagination,
  Tooltip,
  Typography,
  InputBase,
  IconButton
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import { deleteRequest, getRequest, resetDelete, resetIdGet } from '../../../redux/modules/radiology/slice';
import { getReferralList, createReferralOrder, resetSuccessErrorMessage, declienReferralOrder } from '../../../redux/modules/referral/slice';
import ConfirmDialog from '../../../component/ConfirmDialog';
import DeclineOrderDialog from '../../../component/DeclineOrderDialog';
import { generateFullDate, getTime } from '../../../helper/dateTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { getdeclineReferralList } from "../../../redux/modules/dashboard/slice";
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeclinedOrderDetails from './component/declinedOrderDetails';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';

// Modern styled components
const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

const FilterSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  padding: '4px 16px',
  border: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    justifyContent: 'center',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  padding: '4px 16px',
  border: `1px solid ${theme.palette.divider}`,
  maxWidth: 400,
  transition: 'all 200ms ease',
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    width: '100%',
  },
}));

const SearchInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  marginLeft: theme.spacing(1),
  fontSize: '0.9375rem',
  '& input': {
    padding: '8px 0',
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  backgroundColor: theme.palette.background.paper,
  overflowX: 'auto',
  overflowY: 'visible',
  WebkitOverflowScrolling: 'touch',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.dark,
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.8125rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '14px 16px',
    whiteSpace: 'nowrap',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '0.875rem',
    padding: '12px 16px',
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 150ms ease',
  '&:nth-of-type(odd)': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const ExamChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.dark,
  fontWeight: 500,
  fontSize: '0.75rem',
  height: 26,
  margin: '2px',
  '& .MuiChip-label': {
    padding: '0 10px',
  },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.success.main, 0.1),
  color: theme.palette.success.dark,
  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
  fontWeight: 500,
  fontSize: '0.75rem',
  height: 26,
}));

const ActionButton = styled(IconButton)(({ theme, color }) => ({
  padding: 8,
  borderRadius: 8,
  transition: 'all 150ms ease',
  color: color === 'error' ? theme.palette.error.main : theme.palette.primary.dark,
  border: `1px solid ${color === 'error' ? alpha(theme.palette.error.main, 0.3) : alpha(theme.palette.primary.main, 0.3)}`,
  '&:hover': {
    backgroundColor: color === 'error'
      ? alpha(theme.palette.error.main, 0.1)
      : alpha(theme.palette.primary.main, 0.1),
  },
}));

const AddButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  borderRadius: 10,
  padding: '10px 20px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 2px 8px rgba(105, 203, 249, 0.3)',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: '0 4px 12px rgba(105, 203, 249, 0.4)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
  '& .MuiFormControlLabel-label': {
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  '& .MuiRadio-root': {
    color: theme.palette.text.secondary,
    '&.Mui-checked': {
      color: theme.palette.primary.main,
    },
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiFormControlLabel-label': {
      fontSize: '0.75rem',
    },
    '& .MuiFormControlLabel-root': {
      marginRight: theme.spacing(1),
    },
  },
}));

const StyledPagination = styled(Pagination)(({ theme }) => ({
  marginTop: theme.spacing(3),
  '& .MuiPaginationItem-root': {
    borderRadius: 8,
    fontWeight: 500,
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  color: theme.palette.text.secondary,
}));

let pageCount = 0;

const ReferredList = ({ setItemData, toggleList, setShowList }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [viewRadiologyModal, setViewRadiologyModal] = useState({
    openViewRadiologyModal: false,
    orderDetails: {}
  });
  const [selectedOption, setSelectedOption] = useState('refeeredBy');
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [selectedPatientGender, setSelectedPatientGender] = useState(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const currentBranch = useSelector(state => state.branch.currentBranch);
  const referralList = useSelector(state => state.referral.referralList);
  const referral = useSelector(state => state.referral);

  const [tableColumns, setTableColumns] = useState(['Patient Name', 'Exams', 'Referred From', 'Created Date', 'Time', 'Action']);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
    if (event.target.value === 'declinedOrder') {
      setTableColumns(['Patient Name', 'Exams', 'Referred From', 'Referred To', 'Created Date', 'Time', 'Action']);
    } else if (event.target.value === 'refeeredBy') {
      setTableColumns(['Patient Name', 'Exams', 'Referred From', 'Created Date', 'Time', 'Action']);
    } else {
      setTableColumns(['Patient Name', 'Exams', 'Referred To', 'Created Date', 'Time']);
    }
  };

  useEffect(() => {
    dispatch(getReferralList({
      searchBy: selectedOption, page, limit, searchQuery: debouncedSearch
    }));
  }, [selectedOption, currentBranch?.id, page, debouncedSearch]);

  const renderExamList = (exams) => {
    const allExams = exams.flatMap(exam => exam.list.map(subExam => subExam.name));
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {allExams.map((exam, index) => (
          <ExamChip label={exam} key={`${exam}-${index}`} size="small" />
        ))}
      </Box>
    );
  };

  const renderStatus = (patient) => {
    if (patient.orderId) {
      return <StatusChip label="Order Created" size="small" />;
    } else {
      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Accept Order" arrow>
            <ActionButton
              onClick={() => {
                setSelectedPatientGender(patient?.patientId?.gender);
                setShowConfirmationDialog(true);
                setSelectedRecordId(patient._id);
              }}
            >
              <CheckCircleOutlineIcon fontSize="small" />
            </ActionButton>
          </Tooltip>
          <Tooltip title="Decline Order" arrow>
            <ActionButton
              color="error"
              onClick={() => {
                setShowDeclineDialog(true);
                setSelectedRecordId(patient._id);
              }}
            >
              <HighlightOffIcon fontSize="small" />
            </ActionButton>
          </Tooltip>
        </Box>
      );
    }
  };

  const createNewOrderHandler = async () => {
    dispatch(resetIdGet());
    navigate(`/admin/radiology?radiologyId=${selectedRecordId}`, { state: { gender: selectedPatientGender } });
  };

  const cancelNewOrderHandler = () => {
    setShowConfirmationDialog(false);
    setShowDeclineDialog(false);
    setSelectedRecordId(null);
    setSelectedPatientGender(null);
  };

  const declineNewOrderHandler = async (data) => {
    try {
      const payload = { radiologyId: selectedRecordId, ...data, denialTimeString: new Date() };
      await dispatch(declienReferralOrder(payload));
      cancelNewOrderHandler();
      await dispatch(getReferralList({
        searchBy: selectedOption, page, limit, searchQuery: debouncedSearch
      }));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (referral.success === true) {
      toast(referral.successMsg);
    }
    if (referral.success === false) {
      toast(referral.errMesg);
    }
    setTimeout(() => {
      dispatch(resetSuccessErrorMessage());
    }, 5000);
  }, [referral.successMsg]);

  if (referralList?.dataCount && referralList?.dataCount > limit) {
    pageCount = referralList?.dataCount / limit;
    if (pageCount % 2 !== 0) {
      pageCount = Math.ceil(pageCount);
    }
  }

  const displayOrderDetailHandler = async (order) => {
    setViewRadiologyModal({
      openViewRadiologyModal: true,
      orderDetails: order
    });
  };

  return (
    <Box>
      <DeclinedOrderDetails
        viewRadiologyModal={viewRadiologyModal}
        setViewRadiologyModal={setViewRadiologyModal}
      />
      <ConfirmDialog
        showConfirmationDialog={showConfirmationDialog}
        message={'Are you sure that you want to create a new order out of this referral?'}
        handleConfirmDialogClose={cancelNewOrderHandler}
        handleConfirmDialogConfirm={createNewOrderHandler}
      />
      <DeclineOrderDialog
        showConfirmationDialog={showDeclineDialog}
        message={'Are you sure that you want to decline this order out of this referral?'}
        handleConfirmDialogClose={cancelNewOrderHandler}
        handleConfirmDialogConfirm={declineNewOrderHandler}
      />

      {/* Filter Options */}
      <PageHeader>
        <FilterSection>
          <StyledRadioGroup row value={selectedOption} onChange={handleOptionChange}>
            <FormControlLabel value="declinedOrder" control={<Radio size="small" />} label="Declined" />
            <FormControlLabel value="refeeredBy" control={<Radio size="small" />} label="Incoming" />
            <FormControlLabel value="refeeredTo" control={<Radio size="small" />} label="Outgoing" />
          </StyledRadioGroup>
        </FilterSection>
        <AddButton
          startIcon={<AddIcon />}
          onClick={() => { toggleList(); setItemData({}); dispatch(resetIdGet()); dispatch(setShowList(false)); }}
        >
          Add Referral
        </AddButton>
      </PageHeader>

      {/* Search */}
      <Stack mb={3} direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
        <SearchContainer>
          <SearchIcon sx={{ color: 'text.secondary' }} />
          <SearchInput
            placeholder="Search by patient name..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
          />
        </SearchContainer>
      </Stack>

      {/* Table */}
      <Box sx={{ overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
      <StyledTableContainer component={Paper}>
        <Table sx={{ minWidth: { xs: 600, md: 800 } }}>
          <TableHead>
            <TableRow>
              {tableColumns.map((heading, index) => (
                <StyledTableCell key={index}>{heading}</StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {referralList && referralList?.data && referralList?.data.length > 0 ? (
              referralList?.data.map(patient => (
                <StyledTableRow key={patient._id}>
                  <StyledTableCell>
                    <Typography sx={{ fontWeight: 500 }}>
                      {patient.patientId.fName} {patient.patientId.lName}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    {renderExamList(patient.examList)}
                  </StyledTableCell>
                  {selectedOption === 'declinedOrder' ? (
                    <>
                      <StyledTableCell>{patient.createdByBranch.name}</StyledTableCell>
                      <StyledTableCell>{patient.refferedToBranch.name}</StyledTableCell>
                    </>
                  ) : (
                    <StyledTableCell>
                      {selectedOption === 'refeeredBy' ? patient.createdByBranch.name : patient.refferedToBranch.name}
                    </StyledTableCell>
                  )}
                  <StyledTableCell>
                    <Typography sx={{ fontWeight: 500 }}>
                      {generateFullDate(patient.createdAt)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {getTime(patient.createdAt)}
                    </Typography>
                  </StyledTableCell>
                  {selectedOption === 'refeeredBy' && (
                    <StyledTableCell>{renderStatus(patient)}</StyledTableCell>
                  )}
                  {selectedOption === 'declinedOrder' && (
                    <StyledTableCell>
                      <Tooltip title="View Details" arrow>
                        <ActionButton onClick={() => displayOrderDetailHandler(patient)}>
                          <VisibilityIcon fontSize="small" />
                        </ActionButton>
                      </Tooltip>
                    </StyledTableCell>
                  )}
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={tableColumns.length}>
                  <EmptyState>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>No referrals found</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                      Referrals will appear here once created
                    </Typography>
                  </EmptyState>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
      </Box>

      {(referralList?.dataCount > limit) && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <StyledPagination
            count={pageCount}
            page={page}
            onChange={(event, value) => { setPage(value); }}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default ReferredList;
